import { userService } from "api/services/UserService";
import Button from "components/Button";
import { useAuth } from "contexts/AuthContext";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { isPasswordRequiredError, PASSWORD_REQUIRED_USER_MESSAGE } from "utils/passwordRequired";
import { UserProfile } from "types/userProfile";
import PrivacySettingsSection from "components/Profile/PrivacySettingsSection";
import ProfileContactSummary from "components/Profile/ProfileContactSummary";
import "./Profile.css";

type PendingVerification = {
  changeType: 'email' | 'phone';
  sentTo: string;
};

export default function ProfileInformationForm() {

    const { user, refreshUser } = useAuth();
    const navigate = useNavigate();
    const [profile, setProfile] = useState<UserProfile>({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user?.email || '',
        phone: user?.phoneNumber || '',
        about: user?.description || ''
      });
      const [newEmail, setNewEmail] = useState<string>('');
      const [newPhone, setNewPhone] = useState<string>('');
      const [about, setAbout] = useState<string>(profile.about);
      const [password, setPassword] = useState<string>('');
      const [verificationCode, setVerificationCode] = useState<string>('');
      const [pendingVerification, setPendingVerification] = useState<PendingVerification | null>(null);
      const [confirming, setConfirming] = useState<boolean>(false);
    
    const handleProfileUpdate = (e: React.FormEvent) => {
        e.preventDefault();
        if (!user) return;
    
        userService.updateUser(user.id, profile)
        .then((result) => {
          if (result.isSuccess && result.data) {
            setProfile({
              firstName: result.data.firstName || '',
              lastName: result.data.lastName || '',
              email: result.data.email || '',
              phone: result.data.phoneNumber || '',
              about: result.data.description || ''
            });
          }
        })
        .then(() => alert('Profil bilgileri başarıyla güncellendi.'))
        .catch(() => alert('Profil bilgileri güncellenemedi. Lütfen tekrar deneyin.'));
      };
    
    const handleSecurityUpdate = (e: React.FormEvent) => {
      e.preventDefault();
      if (!user) return;

      if (!newEmail.trim() && !newPhone.trim()) {
        alert('Lütfen yeni e-posta veya telefon numarası girin.');
        return;
      }

      userService.updateUserSecurity(user.id, {
        password: password,
        email: newEmail.trim() || undefined,
        phoneNumber: newPhone.trim() || undefined
      })
      .then((result) => {
        if (result.isSuccess && result.data?.requiresVerification) {
          setPendingVerification({
            changeType: result.data.changeType === 'phone' ? 'phone' : 'email',
            sentTo: result.data.verificationSentTo || '',
          });
          setVerificationCode('');
          setPassword('');
          alert(`Doğrulama kodu ${result.data.verificationSentTo || 'e-posta adresinize'} gönderildi.`);
        }
      })
      .catch((err) => {
        if (isPasswordRequiredError(err)) {
          const go = window.confirm(`${PASSWORD_REQUIRED_USER_MESSAGE}\n\nŞifre oluşturma sayfasına gitmek ister misiniz?`);
          if (go) navigate('/sifre-olustur?from=/profil');
          return;
        }
        const message = axios.isAxiosError(err)
          ? (err.response?.data as { message?: string })?.message
          : null;
        alert(message ?? 'Güvenlik bilgileri güncellenemedi. Lütfen tekrar deneyin.');
      });
    };

    const handleConfirmVerification = (e: React.FormEvent) => {
      e.preventDefault();
      if (!user || !pendingVerification) return;

      const code = verificationCode.trim();
      if (code.length !== 6 || !/^\d{6}$/.test(code)) {
        alert('Lütfen 6 haneli doğrulama kodunu girin.');
        return;
      }

      setConfirming(true);
      userService.confirmUserSecurityChange(user.id, { verificationCode: code })
        .then(async (result) => {
          if (result.isSuccess && result.data) {
            setProfile({
              firstName: result.data.firstName || '',
              lastName: result.data.lastName || '',
              email: result.data.email || '',
              phone: result.data.phoneNumber || '',
              about: result.data.description || ''
            });
            setNewEmail('');
            setNewPhone('');
            setVerificationCode('');
            setPendingVerification(null);
            await refreshUser();
            alert('Güvenlik bilgileri başarıyla güncellendi.');
          }
        })
        .catch(() => alert('Doğrulama kodu hatalı veya süresi dolmuş olabilir.'))
        .finally(() => setConfirming(false));
    };

    const handleCancelVerification = () => {
      setPendingVerification(null);
      setVerificationCode('');
    };

    return (
        <>
        <form onSubmit={handleProfileUpdate}>
              <div className="card mb-4">
                <div className="card-body">
                  <h5 className="card-title mb-4">Profil Bilgileri</h5>
                  <div className="row">
                    <div className="col-md-6">
                      <label className="form-label">
                        Ad
                      </label>
                      <input 
                        className="form-control bg-light" 
                        readOnly 
                        type="text" 
                        value={profile.firstName}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">
                        Soyad
                      </label>
                      <input 
                        className="form-control bg-light" 
                        readOnly 
                        type="text" 
                        value={profile.lastName}
                      />
                    </div>
                  </div>
                  
                  <div className="mb-3 mt-3">
                    <label className="form-label">
                      Hakkımda
                    </label>
                    <textarea 
                      className="form-control" 
                      rows={3}
                      value={profile.about}
                      onChange={(e) => setProfile({...profile, about: e.target.value})}
                    ></textarea>
                  </div>
                  <Button className="btn btn-primary" type="submit">
                    Değişiklikleri Kaydet
                  </Button>
                </div>
              </div>
            </form>

            <ProfileContactSummary email={profile.email} phone={profile.phone} />

            <div className="card mb-4" id="profile-contact-edit">
              <div className="card-body">
                <h5 className="card-title mb-4">Güvenlik Ayarları</h5>

                {user?.hasPassword === false && (
                  <div className="alert alert-warning">
                    Google ile giriş yaptığınız için hesabınızda henüz şifre yok. Telefon veya e-posta
                    değiştirmek için önce{' '}
                    <Link to="/sifre-olustur?from=/profil">şifre oluşturmalısınız</Link>.
                  </div>
                )}

                {pendingVerification ? (
                  <form onSubmit={handleConfirmVerification}>
                    <div className="alert alert-info">
                      {pendingVerification.changeType === 'email'
                        ? 'Yeni e-posta adresinize gönderilen 6 haneli doğrulama kodunu girin.'
                        : 'Mevcut e-posta adresinize gönderilen 6 haneli doğrulama kodunu girin.'}
                      {pendingVerification.sentTo && (
                        <div className="mt-1 small">
                          Kod gönderildi: <strong>{pendingVerification.sentTo}</strong>
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        Doğrulama Kodu
                      </label>
                      <input
                        className="form-control form-control-lg text-center"
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        placeholder="000000"
                        value={verificationCode}
                        onChange={(e) => {
                          const v = e.target.value.replace(/\D/g, '');
                          if (v.length <= 6) setVerificationCode(v);
                        }}
                        required
                      />
                    </div>
                    <Button className="btn btn-primary me-2" type="submit" loading={confirming}>
                      Doğrula ve Kaydet
                    </Button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={handleCancelVerification}
                    >
                      İptal
                    </button>
                  </form>
                ) : (
                <form onSubmit={handleSecurityUpdate}>
                  <div className="mb-3">
                    <label className="form-label">
                      Mevcut E-posta
                    </label>
                    <input 
                      className="form-control bg-light" 
                      disabled 
                      type="email" 
                      value={profile.email}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">
                      Yeni E-posta
                    </label>
                    <input 
                      className="form-control" 
                      required={false}
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">
                      Mevcut Telefon Numarası
                    </label>
                    <input 
                      className="form-control bg-light" 
                      disabled 
                      type="tel" 
                      value={profile.phone || 'Henüz bir telefon numarası eklenmedi'}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">
                      Yeni Telefon Numarası
                    </label>
                    <input 
                      className="form-control" 
                      placeholder="05XX XXX XX XX" 
                      type="tel"
                      required={false}
                      value={newPhone}
                      onChange={(e) => setNewPhone(e.target.value)}
                    />
                    <div className="form-text">
                      Telefon numarası değişikliği için doğrulama kodu e-posta adresinize gönderilir.
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">
                      Şifreniz
                    </label>
                    <input 
                      className="form-control" 
                      required 
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <div className="form-text">
                      Güvenliğiniz için mevcut şifrenizi girmeniz gerekmektedir.
                    </div>
                  </div>
                  <div className="sb-form-actions">
                    {user?.hasPassword === false ? (
                      <Link to="/sifre-olustur?from=/profil" className="btn btn-outline-primary">
                        <i className="fas fa-key me-2"></i>Şifre Oluştur
                      </Link>
                    ) : (
                      <Link to="/sifre-degistir" className="btn btn-outline-primary">
                        <i className="fas fa-key me-2"></i>Şifre Değiştir
                      </Link>
                    )}
                    <Button className="btn btn-primary" type="submit">
                      Doğrulama Kodu Gönder
                    </Button>
                  </div>
                </form>
                )}
              </div>
            </div>

            <PrivacySettingsSection />
        </>
    )
}
